import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from "@auth0/angular-jwt";
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from "rxjs";
import { StoreService } from "../services/store.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  public jwtHelper: JwtHelperService = new JwtHelperService();
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private storeService: StoreService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    if (request.headers.has('skipAuthHandling')) {
      return next.handle(request);
    }

    // If skipSettingAccessToken is detected it will not set the bearer, this is used by the getAccessToken function.
    if (request.headers.has('skipSettingAccessToken')) {
      return next.handle(request);
    }
    const token = localStorage.getItem('accessToken');
    if (token) {
      request = AuthInterceptor.setBearerTokenHeader(request, token)
    }

    return next.handle(request).pipe(catchError((error: { status: number; }) => {
      if (error.status === 401) {
        return this.handle401UnauthorizedError(request, next);
      } else if (error.status === 429) {
        this.storeService.logoutUser();
        return next.handle(request);
      } else if (error.status === 0) {
        return next.handle(request);
      } else {
        return throwError(() => error);
      }
    }));
  }

  private static setBearerTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  private handle401UnauthorizedError(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.storeService.getAccessToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          return next.handle(AuthInterceptor.setBearerTokenHeader(request, token));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          // If refresh token is also expired, the user is already logged out by StoreService.getAccessToken()
          // Just return the error to avoid infinite loops
          return throwError(() => error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(AuthInterceptor.setBearerTokenHeader(request, token));
        })
      );
    }
  }
}
